import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * Buy buttons that enter a loading state when this picker dispatches
 * `VariantSelectedEvent` — the main Add to Cart and the sticky bar both set
 * `data-loading` on themselves in response to it.
 */
const LOADING_BUY_BUTTONS = '[ref="addToCartButton"]';

/**
 * State classes that mean the button must stay unclickable. Mirrors the
 * disabled state `applyAtcState` pairs with each class in atc-utils.js —
 * "select-size" is deliberately absent, since that state stays clickable so
 * pressing it can prompt for a size.
 */
const BLOCKED_ATC_CLASSES = ['add-to-cart--sold-out', 'add-to-cart--unavailable', 'add-to-cart--coming-soon'];

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /**
   * Tracks a pointerdown that started on a swatch label, so pointerup can decide
   * whether it was a genuine tap (see #handleTapSelect).
   * @type {{ label: HTMLElement, x: number, y: number } | null}
   */
  #tapStart = null;

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));

    // Selection can't depend on the synthesized `click` alone. On a plugin-heavy
    // PDP the click is intermittently dropped — pointerdown+pointerup fire on the
    // swatch but no click follows, so the native <label>->radio activation never
    // runs and no `change` fires, and the tap appears to do nothing. pointerup is
    // reliable, so drive selection from it too: on a genuine tap, check the radio
    // and dispatch `change` (handled by variantChanged above). Idempotent with the
    // native path — when the click does fire, the radio is already checked, so no
    // duplicate change. Guarded against scroll/drag by tracking the pointerdown.
    this.addEventListener('pointerdown', this.#trackTapStart, { passive: true });
    this.addEventListener('pointerup', this.#handleTapSelect, { passive: true });

    this.#resizeObserver.observe(this);

    // Restore remembered size from sessionStorage (runs before the no-preselect check)
    const restored = this.#restoreRememberedSize();

    // If no size is preselected AND we didn't restore one, signal that no variant is active
    if (this.hasAttribute('data-no-size-preselect') && !restored) {
      const section = this.closest(`[data-section-id="${this.dataset.sectionId}"]`) || this.closest('section');
      if (section) {
        requestAnimationFrame(() => {
          this.dispatchEvent(
            new VariantUpdateEvent(null, '', {
              html: document.implementation.createHTMLDocument(),
              productId: this.dataset.productId ?? '',
            })
          );
        });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  // ── Tap-select fallback (dropped-click resilience) ────────────

  /**
   * Records a pointerdown that lands on a swatch label as a potential tap.
   * @param {PointerEvent} event
   */
  #trackTapStart = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const label = target.closest?.('.variant-option__button-label');
    this.#tapStart = label
      ? { label: /** @type {HTMLElement} */ (label), x: event.clientX, y: event.clientY }
      : null;
  };

  /**
   * On pointerup, if the gesture was a genuine tap of the same swatch label it
   * started on (no meaningful drag), select its radio and dispatch `change`.
   * This makes selection independent of the synthesized click, which is the event
   * that gets dropped. A no-op when the radio is already checked (native path
   * fired) or disabled.
   * @param {PointerEvent} event
   */
  #handleTapSelect = (event) => {
    const start = this.#tapStart;
    this.#tapStart = null;
    if (!start) return;

    const target = /** @type {HTMLElement} */ (event.target);
    if (target.closest?.('.variant-option__button-label') !== start.label) return;
    if (Math.abs(event.clientX - start.x) > 10 || Math.abs(event.clientY - start.y) > 10) return;

    const input = /** @type {HTMLInputElement | null} */ (
      start.label.querySelector('input[type="radio"]')
    );
    if (input && !input.disabled && !input.checked) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  // ── Remember size across product family navigation ────────────

  static #STORAGE_KEY = 'cartography:remembered-size';

  /**
   * Saves the selected size option to sessionStorage.
   * Only saves "size"-like options when data-remember-size is present.
   * @param {HTMLElement} target - The changed input element.
   */
  #saveRememberedSize(target) {
    if (!this.hasAttribute('data-remember-size')) return;
    if (!(target instanceof HTMLInputElement)) return;

    const fieldset = target.closest('fieldset');
    const optionName = fieldset?.querySelector('legend .variant-option__name')?.textContent?.trim().toLowerCase() || '';
    if (!optionName.includes('size')) return;

    try {
      sessionStorage.setItem(
        VariantPicker.#STORAGE_KEY,
        JSON.stringify({ optionName: target.name?.split('-')[0], value: target.value })
      );
    } catch {
      // sessionStorage may be unavailable (private browsing, quota exceeded)
    }
  }

  /**
   * Restores a remembered size from sessionStorage.
   * If the size exists on this product, selects it (even if OOS → shows "Sold Out").
   * If the size doesn't exist, falls back to "Select a Size".
   * Clears sessionStorage after attempting restore.
   * @returns {boolean} True if a size was successfully restored.
   */
  #restoreRememberedSize() {
    if (!this.hasAttribute('data-remember-size')) return false;
    if (!this.hasAttribute('data-no-size-preselect')) return false;

    let stored;
    try {
      const raw = sessionStorage.getItem(VariantPicker.#STORAGE_KEY);
      if (!raw) return false;
      stored = JSON.parse(raw);
      sessionStorage.removeItem(VariantPicker.#STORAGE_KEY);
    } catch {
      return false;
    }

    if (!stored?.value) return false;

    // Find a matching radio by value within a size-like fieldset
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    for (const fieldset of fieldsets) {
      const legendName = fieldset.querySelector('legend .variant-option__name')?.textContent?.trim().toLowerCase() || '';
      if (!legendName.includes('size')) continue;

      const radios = fieldset.querySelectorAll('input[type="radio"]');
      for (const radio of radios) {
        const input = /** @type {HTMLInputElement} */ (radio);
        if (input.value === stored.value) {
          // Found the size — select it and trigger a variant change
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        }
      }
    }

    // Size doesn't exist on this product — fall back to "Select a Size"
    return false;
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    // Save the selected size to sessionStorage for cross-product-family persistence
    this.#saveRememberedSize(event.target);

    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('.product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    // `variantChanged` already dispatched VariantSelectedEvent, which puts the
    // buy buttons into their loading state. Only a `variantUpdate` takes them
    // out of it, and several paths below finish without dispatching one — an
    // aborted or failed fetch, a response with no variant JSON, no resolvable
    // selected option. Track whether we dispatched so the `finally` can undo
    // the loading state ourselves instead of leaving the spinner running.
    const controller = this.#abortController;
    let dispatchedUpdate = false;

    fetch(requestUrl, { signal: controller.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        // Debug: check state at top of fetch response handler
        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            dispatchedUpdate = true;
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      })
      .finally(() => {
        // A newer selection replaced the controller, so that request owns the
        // loading state now and will resolve it — leave it alone.
        if (dispatchedUpdate || this.#abortController !== controller) return;

        this.#releaseBuyButtonLoading();
      });
  }

  /**
   * Takes the buy buttons out of the loading state they entered on
   * `VariantSelectedEvent`, restoring `disabled` from the state class the button
   * is currently showing. Used when a selection finishes without a
   * `variantUpdate`, so the spinner can't outlive the request that started it.
   */
  #releaseBuyButtonLoading() {
    const scope = this.closest('.shopify-section, dialog, product-card') ?? document;

    for (const element of scope.querySelectorAll(LOADING_BUY_BUTTONS)) {
      const button = /** @type {HTMLButtonElement} */ (element);

      button.removeAttribute('data-loading');
      button.disabled = BLOCKED_ATC_CLASSES.some((className) => button.classList.contains(className));
    }
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    // Capture the currently checked radios before morph so we can restore them.
    // Identify by name+value since data-key may not exist on all radios.
    const checkedRadios = new Set();
    this.querySelectorAll('input[type="radio"]:checked').forEach((radio) => {
      const input = /** @type {HTMLInputElement} */ (radio);
      checkedRadios.add(`${input.name}:${input.value}`);
    });

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });

    // Restore checked state for radios that were checked before the morph.
    // The server response may have unchecked them due to preselect_size=false.
    if (checkedRadios.size > 0) {
      this.querySelectorAll('input[type="radio"]').forEach((radio) => {
        const input = /** @type {HTMLInputElement} */ (radio);
        const id = `${input.name}:${input.value}`;
        if (checkedRadios.has(id)) {
          input.checked = true;
          input.dataset.currentChecked = 'true';
        }
      });
    }
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
